<template>
  <div>
    <!-- el-table and its children comes from Element UI -->
    <!-- http://element.eleme.io/#/en-US/component/table -->
    <!--highlight-current-row -->
    <!-- removed:
      <el-table :data="itemsList" stripe border
      -->

    <el-table
      ref="tableList"
      :data="itemsList"
      :border="true"
      @current-change="onSelectionChanged"
      width="100%"
      :cell-class-name="tableCellClassName"
      :highlight-current-row="highlightCurrentRow"
      :class="getTableClass()"
    >

      <el-table-column
        v-for="(col, colIdx) in getNotFixedMetadataColumns"
        :prop="col.id"
        :key="colIdx"
        :label="translateAndFormatColumnHeader(col.text)"
        :width="getWidth(col)"
        :class-name="getColumnClass(col)"
      >
        <template #header="{ column }">
          <a v-if="hasHeaderLink(col)" href="javascript:void(0);" v-on:click="openHeaderLink(col)" >{{ column.label  }}</a >
          <span v-else> {{ column.label }} </span>
          <span v-if="col.subText"><br/>{{ translateAndFormatColumnHeader(col.subText) }}</span>
        </template>

        <template v-slot="scope"  >
          <div>
            <el-link v-if="isRowFoldable(scope.row) && col.__kind === 'buttonFold'"
              underline="never" 
              :icon="getFoldButtonIcon(scope.row)"
              class="fold-button"
              @click="switchFoldState(scope.row)"
            ></el-link>

            <span v-else-if="col.__kind === 'text'" class="word-wrap">{{
              getItemText(scope.row, col)
            }}</span>

            <div v-else-if="col.__kind === 'statusLabel'">
              <el-tooltip v-if="hasHint(scope.row, col)" placement="top" :effect="getTooltipEffect" >
                  <template #content>
                    <div v-html="getHint(scope.row, col)"></div>
                    <!-- todo - have to implement status for this -->
                    <div></div>
                </template>

                  <span>{{ getItemText(scope.row, col) }}</span>

              </el-tooltip>
              <div v-else>
                <span>{{ getItemText(scope.row, col) }}</span>
              </div>
            </div>

            <div v-else-if="col.__kind === 'textArray'">
              <div
                v-for="(itm, itmIdx) in getArrayItemValues(scope.row, col.id)"
                :key="itmIdx"
              >
                <span>{{ itm }}</span>
              </div>
            </div>

            <a v-else-if="col.__kind === 'link'"
              href="javascript:void(0);"
              v-on:click="openLink(col.formula, scope.row)"
              >{{ getItem(scope.row, col.id) }}</a
            >

            <div v-else-if="col.__kind === 'complex'">
              <div
                v-for="(itm, iidx) in getComplexItems( col, scope.row, scope.row )"
                :key="iidx"
                :class="itm.break == true ? '' : 'complex-item'"
              >
                <a
                  v-if="isLink(itm) && itm.text"
                  href="javascript:void(0);"
                  v-on:click="openLink(itm.value, scope.row)"
                  >{{ itm.text }}</a
                >
                <span v-if="isText(itm)">{{ itm.text }}</span>
                
                <el-input v-if="isInput(itm)" v-model="scope.row[itm.id]" ></el-input>
  
                <br v-if="itm.break == true"/>

              </div>
            </div>
            
            <div v-else-if="col.__kind === 'select' && shouldShow(col, scope.row)">
              <!-- Lazy-edit (opt-in via lazyEditSelects): render the selected
                   option's label as plain text and only mount the real el-select
                   (with its el-option children) when the cell is clicked. Avoids
                   instantiating thousands of el-option components up-front on
                   long lists. Collapses back to text on blur. When lazyEditSelects
                   is false this renders the always-mounted el-select as before. -->
              <span
                v-if="lazyEditSelects && !isEditingSelect(scope.row, col)"
                class="lazy-select-text"
                :class="getItemIfDuplicatedClass(col, scope.row[col.id])"
                tabindex="0"
                @click="startEditSelect(scope.row, col)"
                @keydown.enter.prevent="startEditSelect(scope.row, col)"
                >{{ getSelectOptionLabel(col, scope.row[col.id]) }}</span
              >

              <el-select v-else
                :ref="lazyEditSelects ? (el) => registerEditingSelect(el, scope.row, col) : undefined"
                v-model="scope.row[col.id]"
                @change="onItemChanged(scope.row, col.id)"
                @blur="lazyEditSelects ? onSelectBlur(scope.row, col) : undefined"
                :class="getItemIfDuplicatedClass(col, scope.row[col.id])"
                filterable
              >
                <el-option
                  v-for="option in col.options"
                  :value="option.id"
                  :label="option.text"
                  :key="option.id"
                >
                </el-option>
              </el-select>
            </div>

            <el-select v-else-if="col.__kind === 'multiSelect'"
              v-model="scope.row[col.id]"
              multiple
              filterable
              placeholder="Select"
              @change="onItemChanged(scope.row, col.id)"
              style="min-width: 360px"
            >
              <el-option
                v-for="option in col.options"
                :key="option.id"
                :label="option.text"
                :value="option.id"
              >
              </el-option>
            </el-select>

            <div v-else-if="col.__kind === 'linkArray'">
              <div v-if="shouldShowFolded(scope.row, col.id)" class="folded-item" >
                {{ getFoldedItemText(scope.row, col.id) }}
              </div>
              <div v-else>
                <div v-for="itm in getItem(scope.row, col.id)" :key="itm.id" class="linkarray" >
                  <a href="javascript:void(0);" v-on:click="openLink(col.formula, itm)" :class="'linkarray ' + getArrayItemClass(col, itm)" >
                    {{ getLinkArrayItemText(itm) }}</a >

                  <span v-if="hasSuffix(col)">{{ evaluateMessage(col.suffix, itm) }}</span>
                </div>
              </div>
            </div>

            <div v-else-if="col.__kind === 'complexArray'">
              <div v-if="shouldShowFolded(scope.row, col.id)" class="folded-item" >
                {{ getFoldedItemText(scope.row, col.id) }}
              </div>
              <div v-if="!shouldShowFolded(scope.row, col.id)">
                <div v-for="arrItem in getItem(scope.row, col.id)" :key="arrItem.id" class="linkarray" >
                  <div v-for="(itm,idx) in getComplexItems(col, arrItem, scope.row)" :key="idx" class="complex-item" >
                    <a v-if="isLink(itm) && itm.text" href="javascript:void(0);" v-on:click="openLink(itm.value, arrItem)" >{{ itm.text }} </a>
                    <span v-if="isText(itm)">{{ itm.text }}</span>
                    <div v-else-if="isStatusLabel(itm)" :class="'complex-array-item ' + getStatusLabelClass(itm)">
                      <el-tooltip v-if="hasHint(itm, col)" placement="top" :effect="getTooltipEffect" >
                          <template #content>
                            <div v-html="getHint(itm, col)"></div>
                            <!-- todo - have to implement status for this -->
                            <div></div>
                          </template>
                          <span>{{ getItemText(itm, {id: 'text'}) }}</span>
                      </el-tooltip>
                      <div v-else>
                        <span>{{ getItemText(itm, {id: 'text'}) }}</span>
                      </div>
                    </div>
                     <div v-if="itm.break == true"><br /></div>

                  </div>
                </div>
                
              </div>
            </div>

            <div v-else-if="col.__kind === 'filesArray'">
              <div
                v-if="shouldShowFolded(scope.row, col.id)"
                class="folded-item"
              >
                {{ getFoldedItemText(scope.row, col.id) }}
              </div>
              <div v-if="!shouldShowFolded(scope.row, col.id)">
                <cc-files-list
                  :files="getItem(scope.row, col.id)"
                  :propName="col.id"
                  :readonly="true"
                  :formula="col.formula"
                  :entity="scope.row"
                ></cc-files-list>
              </div>
            </div>

            <el-input v-else-if="col.__kind === 'input'"
              v-model="scope.row[col.id]"
              @change="onItemChanged(scope.row, col.id)"
            ></el-input>

            <el-input-number v-else-if="col.__kind === 'decimal'"
              v-model="scope.row[col.id]"
              controls-position="right"
              type="number"
              :step="getPrecision"
            ></el-input-number>

            <div v-else-if="col.__kind === 'numeric'" style="display: block">
              <el-input-number
                v-model="scope.row[col.id]"
                controls-position="right"
                size="small"
                style="width: 112px"
                @change="onItemChanged(scope.row, col.id)"
              />
              <span v-if="col.suffix">{{ col.suffix }}</span>
            </div>

            <span v-else-if="col.__kind === 'calculated'" class="word-wrap">{{
              evaluateFormula(col.formula, scope.row, col.id)
            }}</span>

            <span v-else-if="col.__kind === 'calculatedHidden'" style="display: none">{{
              evaluateFormula(col.formula, scope.row, col.id)
            }}</span>

            <div v-else-if="col.__kind === 'progress'" style="text-align: center;">
              <el-tooltip placement="top">
                <template #content>
                    <div v-html="col.hint ? getHint(scope.row, col) : `${cappedScheduleScore(scope.row[col.id])}%`"></div>
                    <!-- todo - have to implement status for this -->
                    <div></div>
                </template>
                <el-progress :percentage="cappedScheduleScore(scope.row[col.id])" 
                  :type="col.type=='progressCircle' ? 'circle' : undefined"
                  :text-inside="false"
                  :show-text="col.type!='progressCircle'"
                  :format="col.formula ? (val) => evaluateMessage(col.formula, scope.row) : undefined"
                  :color="getProgressPercentageColor(scope.row[col.id],col.colorMap)" :width="40"> 
                </el-progress>
              </el-tooltip>
            </div>
            
          </div>
        </template>
      </el-table-column>

      <el-table-column
        v-if="!hideActions"
        label=""
        :width="getWidth({ id: 'action-buttons' })"
      >
        <template v-slot="scope">
          <div class="column-actions-wrapper">
            <div class="column-actions">
              <el-button
                v-if="hasAllowedAction('moveUp', scope.row) && !readOnly"
                @click="onAction('moveUp', scope.row)"
                :type="listButtonType"
                :disabled="isFirst(scope.row)"
                :title="getActionTitle('moveUp', scope.row)"
                :icon="ArrowUp"
                size="small"
              />

              <el-button
                v-if="hasAllowedAction('moveDown', scope.row) && !readOnly"
                @click="onAction('moveDown', scope.row)"
                :type="listButtonType"
                :disabled="isLast(scope.row)"
                :title="getActionTitle('moveDown', scope.row)"
                :icon="ArrowDown"
                size="small"
              />

              <el-button
                v-if="hasAllowedItemAction('start', scope.row) && !readOnly && !scope.row.isBlocked"
                @click="confirmActionExecution('start', scope.row)"
                :type="listButtonType"
                :title="getActionTitle('start', scope.row)"
                :icon="CaretRight"
                size="small"
              />

              <el-button
                v-if="hasAllowedItemAction('pause', scope.row) && !readOnly && !scope.row.isBlocked"
                @click="confirmActionExecution('pause', scope.row)"
                :type="listButtonType"
                :title="getActionTitle('pause', scope.row)"
                :icon="VideoPause"
                size="small"
              />

              <el-button
              v-if="hasAllowedItemAction('complete', scope.row) && !readOnly && !scope.row.isBlocked"
                @click="confirmActionExecution('complete', scope.row)"
                :type="listButtonType"
                :title="getActionTitle('complete', scope.row)"
                :icon="Check"
                size="small"
              />

              <el-button
                v-if="hasAllowedItemAction('restore', scope.row) && !readOnly"
                @click="confirmActionExecution('restore', scope.row)"
                :type="listButtonType"
                :title="getActionTitle('restore', scope.row)"
                :icon="Refresh"
                size="small"
              />

              <el-button
                v-if="hasAllowedAction('edit')"
                @click="edit(scope.row)"
                :type="listButtonType"
                :icon="Edit"
                size="small"
                :title="getActionTitle('edit', scope.row)"
              />

              <cc-actions
                :allowedActions="getAllowedActions"
                :entity="scope.row"
                @onAction="onAction"
                :isListActionButtons="true"
              />

              <el-button
                v-if="hasAllowedAction('print')"
                :icon="Printer"
                size="small"
                style="margin-left: 10px"
                :title="translate('Print')"
                @click="$emit('print', scope.row)"
              />

              <el-button
                v-if="hasAllowedAction('complete-external-cooperation')"
                :icon="Check"
                size="small"
                type="primary"
                :title="translate('Complete external processing')"
                @click="
                  confirmActionExecution(
                    'complete-external-cooperation',
                    scope.row
                  )
                "
              />

              <el-button
                v-if="hasAllowedAction('delete') && !readOnly"
                @click="confirmActionExecution('delete', scope.row)"
                type="danger"
                :title="getActionTitle('delete', scope.row)"
                :icon="Delete"
                size="small"
                style="margin-left: 10px"
              />
            </div>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <cc-totals
      v-if="hasTotals"
      :metadata="getTotalsMetadata"
      :entities="items"
      notExpandable="true"
    />

    <!-- for totals not to overlap everything -->
    <div style="height: 72px" />

    <div v-if="hasAllowedAction('new')" :class="getFooterClass()">
      <el-button v-if="!readOnly" @click="create" type="primary">{{
        translate('New')
      }}</el-button>
    </div>

    <div v-if="hasAllowedAction('create')" :class="getFooterClass()">
      <el-button v-if="!readOnly" @click="onAction('create')" type="primary">{{
        translate('Create')
      }}</el-button>
    </div>

    <div
      v-if="hasRelatedItems"
      :class="getFooterClass() + ' related-items-wrapper'"
    >
      <el-link
        :icon="View"
        type="primary"
        @click="showRelatedItems"
        class="related-items-link"
        >{{ getRelatedItemsText }}</el-link
      >
    </div>
  </div>
</template>


<script>

import Vue, { markRaw } from 'vue'
import { mapActions, mapGetters } from 'vuex'
//import actions from '../../store/actions'
import tierGuard from '../../mixins/tierGuard'

import {
  ArrowUp,
  ArrowDown,
  CaretRight,
  VideoPause,
  Check,
  Refresh,
  Edit,
  Printer,
  Delete,
  View,
} from '@element-plus/icons-vue'

const ProgressColors = {
  green: '#67C23A',
  orange: '#E6A23C',
  red: '#F56C6C',
};

// Precompiled-formula cache. Formula strings coming from metadata (col.formula,
// col.showIfFormula, col.hint, col.suffix, objectAction.*, link formulas, ...)
// used to be run through eval() on every render of every cell. eval() is slow
// and, worse, de-optimizes the entire surrounding method (V8 won't JIT a
// function that contains a direct eval). Here we compile each distinct formula
// string once into a real Function and memoize it by the string itself, so
// render-path methods call a cached function instead of eval(). A module-level
// Map (rather than a per-column stamp) is used because the same formula sources
// are not all owned by a column — object-action messages and ad-hoc strings
// flow through the same path. Compilation failures are cached as null so a bad
// formula isn't recompiled on every render.
const __formulaFnCache = new Map()

// Superset signature: every call site passes the aliases it has in scope and
// `undefined` for the rest, so the existing formula strings (which reference
// rootEntity / item / that / col / column / row / items in different
// combinations) keep working verbatim — no rewriting of server-side formula
// definitions needed.
//
// IMPORTANT: callers must invoke the returned function with the component bound
// as `this` — `fn.call(componentThis, ...args)`. Under the old eval() the
// formula ran inside the method, so `this` was the component; formulas call
// component methods both as `this.translate(...)` and via the `that` alias.
// Function-constructor functions otherwise run sloppy with `this === window`,
// which would break every `this.*` reference.
function compileFormula(formula) {
  if (!formula) return null
  if (__formulaFnCache.has(formula)) return __formulaFnCache.get(formula)

  let fn = null
  try {
    // eslint-disable-next-line no-new-func
    fn = new Function(
      'rootEntity',
      'item',
      'that',
      'col',
      'column',
      'row',
      'items',
      // Wrap in parens with newlines: `return (` keeps the open paren on the
      // same line as `return` so a formula that starts with a newline (every
      // C# $@"…" multi-line IIFE formula does) is NOT cut short by Automatic
      // Semicolon Insertion — `return\n(function(){…})()` would otherwise parse
      // as `return;` and yield undefined. The trailing newline before `)`
      // guards a formula that ends in a `// line comment`.
      'return (\n' + formula + '\n)'
    )
  } catch (e) {
    console.error('ERROR: compileFormula: ' + formula)
    console.error(e.message)
    fn = null
  }

  __formulaFnCache.set(formula, fn)
  return fn
}

export default {
  data() {
    return {
      selectedItemIdHasBeenApplied: false,
      // Key (`${row.id}__${col.id}`) of the single select cell currently in
      // edit mode for lazyEditSelects; null = all selects collapsed to text.
      editingSelectKey: null,
      ArrowUp: markRaw(ArrowUp),
      ArrowDown: markRaw(ArrowDown),
      CaretRight: markRaw(CaretRight),
      VideoPause: markRaw(VideoPause),
      Check: markRaw(Check),
      Refresh: markRaw(Refresh),
      Edit: markRaw(Edit),
      Printer: markRaw(Printer),
      Delete: markRaw(Delete),
      View: markRaw(View),
    }
  },
  name: 'CcVarList',
  mixins: [tierGuard],
  props: {
    items: null, // componentList
    metadata: null, // {itemType: 'Component', allowedActions: [{action: 'edit'}, {action: 'delete'}], columns: [{id: 'id', text: 'ID'}, {id: 'name', text: 'Детайл'}, {id: 'files', text: 'Документи', type: 'filesArray'}]}
    baseroute: '', // components
    sortKey: '',
    createObjectAdditionalQueryParams: null,
    highlightCurrentRow: false,
    selectedItemId: null,
    nonFixedFooter: false,
    startActionOptions: {},
    relatedItems: { text: null, count: 0 },
    hideActions: false,
    readOnly: false,
    maxButtonsCount: '',
    rootEntity: {},
    rightAlign: false,
    // Opt-in: render single-select cells as plain text and only mount the
    // el-select on click (collapsing back on blur). Set by long, perf-sensitive
    // lists (e.g. the workcenters actions list) to avoid eagerly instantiating
    // an el-option component per option per row.
    lazyEditSelects: false,
    // Free-tier guard: when set ('users' | 'workcenters' | 'workers'), the
    // footer "New" button pre-checks the tier limit (tierGuard mixin) before
    // navigating to the create form, and opens the upgrade dialog if at cap.
    tierGuardKind: { type: String, default: '' },
  },
  computed: {
    itemsList() {
      return this.items
    },

    ...mapGetters(['globalMetadata']),


    getNotFixedMetadataColumns() {
      return this.getAllMetadataColumns;
    },

    getAllMetadataColumns() {
      if (!this.metadata || !this.metadata.columns) {
        return []
      }

      // readOnly participates in the kind (e.g. select/numeric/input render as
      // plain text when readOnly), so read it here to register it as a reactive
      // dependency — when it toggles, the columns are re-stamped below.
      const readOnly = this.readOnly

      var result = []
      this.metadata.columns.forEach(c => {
        if(c.showIfFormula) {
          const rootEntity = this.rootEntity || {}
          const item = {}
          const column = c
          const fn = compileFormula(c.showIfFormula)
          const shouldShow = fn ? fn.call(this, rootEntity, item, this, c, column, rootEntity) : true
          if(shouldShow) {
            result.push(c)
          }
        } else {
          result.push(c)
        }
      })

      // Precompute each column's render kind once per metadata/readOnly change
      // and stamp it on the column (like the __unfolded / __isFoldable row
      // stamps). The per-cell template then does a single string compare
      // (col.__kind === 'text') instead of running the ~17 is*() checks for
      // every row × column on every render. This recomputes automatically when
      // metadata is replaced or a column's type is mutated in place (dynamic
      // action type internal↔external switches), since computeColumnKind reads
      // col.type and readOnly, both tracked by this computed.
      result.forEach(c => { c.__kind = this.computeColumnKind(c, readOnly) })

      return result
    },

    isFoldableTable() {
      return (
        this.metadata &&
        this.metadata.columns &&
        this.metadata.columns.length > 0 &&
        this.isButtonFold(this.metadata.columns[0])
      )
    },

    listButtonType() {
      var appSkin = this.globalMetadata ? this.globalMetadata.appSkin : null
      if (appSkin && appSkin.styles['--list-button-type']) {
        return appSkin.styles['--list-button-type']
      }
      return 'default'
    },

    getPrecision() {
      if (
        this.globalMetadata &&
        this.globalMetadata.precision &&
        this.globalMetadata.precision > 0.000001
      ) {
        return this.globalMetadata.precision
      }

      return 0.001
    },

    hasTotals() {
      return !!this.metadata.totals
    },

    getTotalsMetadata() {
      return this.metadata ? this.metadata.totals : null
    },

    duplicatedItemsByCol() {
      var result = {}

      this.getNotFixedMetadataColumns.forEach((col) => {
        if (col.markDuplicated) {
          var singles = new Set()
          var duplicated = []
          this.items.forEach((item) => {
            var arr = item[col.id]
            if (Array.isArray(arr)) {
              arr.forEach((arrItem) => {
                var curr = this.getLinkArrayItemText(arrItem)
                if (singles.has(curr)) {
                  duplicated.push(curr)
                } else {
                  singles.add(curr)
                }
              })
            } else {
              var curr = arr
              if (singles.has(curr)) {
                duplicated.push(curr)
              } else {
                singles.add(curr)
              }
            }
          })

          result[col.id] = duplicated
        }
      })

      return result
    },

    getAllowedActions() {
      if (!this.metadata.allowedActions) {
        return []
      }

      // return this.metadata.allowedActions

      // TEMP TODO: return this.metadata.allowedActions
      var temp = []
      this.metadata.allowedActions.forEach((n) => {
        if (
          // Inquiry actions
          n.action == 'createOrder' ||
          // Material order actions
          n.action == 'requestMaterial' ||
          n.action == 'receiveMaterial' ||
          // order actions
          n.action == 'ordered'
        ) {
          temp.push(n)
        }
      })

      return temp
    },

    hasRelatedItems() {
      return (
        this.relatedItems &&
        this.relatedItems.text &&
        this.relatedItems.text.length > 0 &&
        this.relatedItems.count > 0
      )
    },

    getRelatedItemsText() {
      return this.hasRelatedItems ? this.relatedItems.text : null
    },

    getTooltipEffect() {
      return document.body.style.getPropertyValue('--tooltip-effect') == 'dark'
        ? 'dark'
        : 'light'
    },

    // Memoize the statuses map once. globalMetadata is a Vuex getter and
    // getStatusLabelClass runs per cell (via tableCellClassName, which el-table
    // invokes for every row × column). Reading the getter chain there would be
    // R×C lookups per render; this computed recomputes only when globalMetadata
    // changes.
    statusesMap() {
      return this.globalMetadata ? this.globalMetadata.statuses : null
    },
  },
  watch: {
    // Eagerly warm row.__isFoldable as soon as the rows or the column metadata
    // arrive, so the first render doesn't pay the columns × items walk inside
    // isRowFoldable(). Shallow watchers: they fire on prop replacement (a new
    // fetch result), not on nested mutation, so toggling __unfolded won't
    // re-trigger them. precomputeFoldable() is guarded + idempotent, so it's
    // safe for both immediate firings to run at init.
    items: { immediate: true, handler() { this.precomputeFoldable() } },
    metadata: { immediate: true, handler() { this.precomputeFoldable() } },
  },
  updated() {
    if (
      this.highlightCurrentRow &&
      this.selectedItemId &&
      this.selectedItemId > 0
    ) {
      if (!this.selectedItemIdHasBeenApplied && this.items) {
        var row = this.items.find((n) => n.id == this.selectedItemId)
        if (row) {
          this.$refs.tableList.setCurrentRow(row)
          this.selectedItemIdHasBeenApplied = true
        }
      }
    }
  },
  methods: {
    edit(entity) {
      const { id } = entity
      this.$router.push({ name: `${this.baseroute}.edit`, params: { id } })
    },

    view(entity) {
      const { id } = entity
      this.$router.push({ name: `${this.baseroute}.view`, params: { id } })
    },

    create() {
      // Free-tier pre-check: if this list is guarded and the tenant is at its
      // cap, open the upgrade dialog instead of navigating to the New form.
      if (this.tierGuardKind) {
        this.guardAdd(this.tierGuardKind, () => this.doCreate())
        return
      }
      this.doCreate()
    },

    doCreate() {
      if (!!this.createObjectAdditionalQueryParams) {
        // note: without: JSON.parse(JSON.stringify... doesn't work
        this.$router.push({
          name: `${this.baseroute}.new`,
          query: JSON.parse(
            JSON.stringify(this.createObjectAdditionalQueryParams)
          ),
        })
      } else {
        this.$router.push({ name: `${this.baseroute}.new` })
      }
    },

    hide() {
      this.$router.push({ name: `${this.baseroute}.index` })
    },

    evaluateFormula(formula, item, propName) {
      var rootEntity = item
      var fn = compileFormula(formula)
      if (!fn) return ''
      try {
        var result = fn.call(this, rootEntity, item, this, undefined, undefined, item)
        item[propName] = result
        return result
      } catch (e) {
        console.error('ERROR: evaluateFormula: ' + formula)
        console.error(e.message)
      }

      return ''
    },

    evaluateMessage(messageFormula, item) {
      var result = messageFormula
      if (result.startsWith('`') || result.startsWith("'")) {
        var fn = compileFormula(messageFormula)
        if (fn) {
          try {
            result = fn.call(this, item, item, this, undefined, undefined, item)
          } catch (e) {
            console.error('ERROR: evaluateFormula: ' + messageFormula)
            console.error(e.message)
          }
        }
      }

      return result
    },

    hasHeaderLink(col) {
      return col.headerLinkFormula && col.headerLinkFormula.length > 0
    },

    openHeaderLink(col) {
      const rootEntity = this.rootEntity
      const items = this.items
      var result = null
      var fn = compileFormula(col.headerLinkFormula)
      try {
        result = fn ? fn.call(this, rootEntity, undefined, this, col, col, undefined, items) : null
      } catch (e) {
        console.error(e)
        result = null
      }

      if (result && result.length > 0)
        this.navigateToLink(result)
    },

    openLink(formula, item) {
      if (!formula && item && item.value && item.value.length > 1) {
        this.navigateToLink(item.value)
      } else {
        var fn = compileFormula(formula)
        var path = fn ? fn.call(this, item, item, this, undefined, undefined, item) : formula
        if (path.startsWith('method:')) {
          var method = path.substring('method:'.length)
          this.$emit(
            'linkMethodCallback',
            this.getMethodName(method),
            this.getMethodArgs(method, item)
          )
        } else {
          this.navigateToLink(path)
        }
      }
    },

    getWidth(column) {
      if (this.isButtonFold(column)) {
        return '36'
      } else if (column.id == 'action-buttons') {
        var maxButtonsCount =
          1 * (this.maxButtonsCount ? this.maxButtonsCount : 3)
        return `${maxButtonsCount * 60}`
        // return "180"
      } else if (column.id == 'id') {
        return '80'
      } else if (column.width > 0) {
        return column.width
      }

      return ''
    },

    translateAndFormatColumnHeader(text) {
      if(text && text.startsWith('`')) {
        text = this.evaluateMessage(text);
      }

      text = this.translate(text)
      return text;
    },

    getTableClass() {
      return this.rightAlign ? 'right-align-table' : undefined
    },

    getColumnClass(column) {
      return column.highlight ? 'column-highlight': undefined;
    },

    // Maps a column to the single render branch it should take, in the SAME
    // precedence order as the v-else-if chain in the template. Must stay in
    // sync with that chain (and with the is*() methods, still used for the
    // dynamic per-item checks inside complex/complexArray cells). Returns null
    // for unknown types so no branch renders — matching the previous behavior
    // where no is*() matched.
    computeColumnKind(column, readOnly) {
      const t = column.type

      if (t == 'buttonFold') return 'buttonFold'

      // isText — note readOnly absorbs the editable types into plain text
      if (
        !t ||
        t == '' ||
        t == 'text' ||
        t == 'label' ||
        t == 'dateLabel' ||
        t == 'durationInUserUnits' ||
        (readOnly &&
          (t == 'numeric' ||
            t == 'select' ||
            t == 'multiSelect' ||
            t == 'input' ||
            t == 'decimal'))
      )
        return 'text'

      if (t == 'statusLabel' || t == 'statusDateLabel') return 'statusLabel'
      if (t == 'inputArray' || t == 'inputLinksArray' || t == 'emailArray')
        return 'textArray'
      if (t == 'link') return 'link'
      if (t == 'complex') return 'complex'
      if (t == 'select') return 'select'
      if (t == 'multiSelect') return 'multiSelect'
      if (t == 'linkArray') return 'linkArray'
      if (t == 'complexArray' || t == 'statusComplexArray') return 'complexArray'
      if (t == 'filesArray' || t == 'readonlyFilesArray') return 'filesArray'
      if (t == 'input') return 'input'
      if (t == 'decimal') return 'decimal'
      if (t == 'numeric') return 'numeric'
      if (t == 'calculated') return 'calculated'
      if (t == 'calculatedHidden') return 'calculatedHidden'
      if (t == 'progress' || t == 'progressCircle') return 'progress'

      return null
    },

    isButtonFold(column) {
      return column.type == 'buttonFold'
    },

    isText(column) {
      return (
        !column.type ||
        column.type == '' ||
        column.type == 'text' ||
        column.type == 'label' ||
        column.type == 'dateLabel' ||
        column.type == 'durationInUserUnits' ||
        (this.readOnly && column.type == 'numeric') ||
        (this.readOnly && column.type == 'select') ||
        (this.readOnly && column.type == 'multiSelect') ||
        (this.readOnly && column.type == 'input') ||
        (this.readOnly && column.type == 'decimal')
      )
    },

    isStatusLabel(column) {
      return column.type == 'statusLabel' || column.type == 'statusDateLabel'
    },

    isFilesArray(column) {
      return column.type == 'filesArray' || column.type == 'readonlyFilesArray'
    },

    isTextArray(column) {
      return (
        column.type == 'inputArray' ||
        column.type == 'inputLinksArray' ||
        column.type == 'emailArray'
      )
    },

    isNumeric(column) {
      return column.type == 'numeric' && !this.readOnly
    },

    isComplex(column) {
      return column.type == 'complex'
    },

    isLink(column) {
      return column.type == 'link'
    },

    isLinkArray(column) {
      return column.type == 'linkArray'
    },

    isComplexArray(column) {
      return column.type == 'complexArray' || column.type == 'statusComplexArray'
    },

    isSelect(column) {
      return column.type == 'select' && !this.readOnly
    },

    isMultiSelect(column) {
      return column.type == 'multiSelect' && !this.readOnly
    },

    isInput(column) {
      return column.type == 'input' && !this.readOnly
    },

    isCalculated(column) {
      return column.type == 'calculated'
    },

    isCalculatedHidden(column) {
      return column.type == 'calculatedHidden'
    },

    isDecimal(column) {
      return column.type == 'decimal' && !this.readOnly
    },

    isProgress(column) {
      return column.type == 'progress' || column.type == 'progressCircle'
    },

    cappedScheduleScore(score) {
      // todo - this should not happen
      return Math.min(100, Math.max(0, score));
    },

    getProgressPercentageColor(score, colorMap) {
      const defaultColorMap = ['progressive', 50, 80];
      const parts = colorMap  ? colorMap.split(':')  : defaultColorMap;

      const getProgressivePercentageColor = (score, threshold1, threshold2) => {
        if (score >= 1*threshold2) return  ProgressColors.green;
        if (score >= 1*threshold1) return  ProgressColors.orange;
        return ProgressColors.red; 
      };

      const getDegressivePercentageColor = (score, threshold1, threshold2) => {
        if (score >= 1*threshold2) return  ProgressColors.red;
        if (score >= 1*threshold1) return  ProgressColors.orange;
        return ProgressColors.green; 
      };

      const getPercentageColor = (score, type, threshold1, threshold2) => {
        switch(type) {
          case 'degressive':
            return getDegressivePercentageColor(score, threshold1,threshold2);
          case 'progressive':
          default:
            return getProgressivePercentageColor(score, threshold1,threshold2);
        }
      };

      if(parts.length == 6) {
        return score <= 1*parts[4]// part[4] is beggining of the secondcoloring map
          ? getPercentageColor(score, parts[0],parts[1],parts[2])
          : getPercentageColor(score, parts[3],parts[4],parts[5]);
      } else  {
        return getPercentageColor(score, parts[0],parts[1],parts[2]);
      }
    },

    getArrayItemValues(row, colId) {
      var result = []

      var arr = this.getItem(row, colId)
      if (!arr || !arr.length) {
        return result
      }

      for (var i = 0; i < arr.length; i++) {
        if (arr[i] && !!arr[i].value) {
          result.push(arr[i].value)
        }
      }

      return result
    },

    getComplexItems(col, item/*don't remove - used for js eval*/, rootEntity/*don't remove - used for js eval*/) {
      if (!this.isComplex(col) && !this.isComplexArray(col)) {
        return []
      }

      //console.log('col.formula',col.formula)
      const that = this;
      const fn = compileFormula(col.formula)
      var res = fn ? fn.call(this, rootEntity, item, that, col, col, rootEntity) : null

      //console.log('res',res)

      if (res) {
        for (var i = 0; i < res.length; i++) {
          res[i].id = i
          if (res[i].value) {
            res[i].value = '"' + res[i].value + '"'
          }
        }
      }

      return res
    },

    hasSuffix(col) {
      return col && col.suffix
    },

    shouldShow(col, item) {
      if (item && col.showIfFormula && col.showIfFormula.length > 0) {
        var fn = compileFormula(col.showIfFormula)
        if (fn) {
          try {
            return fn.call(this, item, item, this, col, col, item)
          } catch (e) {
            console.error('ERROR: evaluateFormula: ' + e.message)
          }
        }
      }

      return true
    },

    // --- Lazy-edit select helpers (active only when lazyEditSelects is set) ---

    selectKey(row, col) {
      return `${row.id}__${col.id}`
    },

    isEditingSelect(row, col) {
      return this.editingSelectKey === this.selectKey(row, col)
    },

    // Resolve the label shown in the collapsed (text) state from the current
    // value. Loose compare because option ids may be numbers (priority) or
    // strings (workcenter id) while the row value type can differ.
    getSelectOptionLabel(col, value) {
      if (col.options) {
        const opt = col.options.find((o) => o.id == value)
        if (opt) return opt.text
      }
      return value != null && value !== '' ? `${value}` : ''
    },

    startEditSelect(row, col) {
      this.editingSelectKey = this.selectKey(row, col)
      // After the el-select mounts (registerEditingSelect stashes the instance),
      // focus it and open the dropdown so the click that revealed it also starts
      // the selection — no second click needed.
      this.$nextTick(() => {
        const inst = this.__editingSelectEl
        if (!inst) return
        if (inst.focus) inst.focus()
        const input = inst.$el && inst.$el.querySelector('input')
        if (input) input.click()
      })
    },

    registerEditingSelect(el, row, col) {
      // Function ref: capture only the instance of the cell currently editing.
      if (el && this.isEditingSelect(row, col)) {
        this.__editingSelectEl = el
      }
    },

    onSelectBlur(row, col) {
      if (this.isEditingSelect(row, col)) {
        this.editingSelectKey = null
        this.__editingSelectEl = null
      }
    },

    onSelectionChanged(item) {
      this.$emit('selectionChanged', item)
    },

    showRelatedItems() {
      this.$emit('showRelatedItems')
    },

    getAllowedAction(action) {
      if (!this.metadata.allowedActions) {
        return null
      }

      var act = this.metadata.allowedActions.find(
        (n) => n && n.action == action
      )
      return act
    },

    hasAllowedAction(action) {
      return this.getAllowedAction(action) != null
    },

    getNextStatus(action, item) {
      var act = this.getAllowedAction(action)
      if (act && act.prev && act.prev.length > 0) {

        var idx = act.prev.indexOf(item.status)
        if (idx >= 0) {
          return act.next
        }
      }

      return null
    },

    hasAllowedItemAction(action, entity) {
      var next = this.getNextStatus(action, entity)
      return next != null
    },

    getActionTitle(action, entity) {
      const act = this.getAllowedAction(action)
      var result =
        act && act.buttonTitle
          ? this.evaluateMessage(act.buttonTitle, entity)
          : action

      return this.translate(result)
    },

    confirmActionExecution(action, entity) {
      const item = entity
      var act = this.getAllowedAction(action)
      if (!act && action == 'start') {
        act = { title: 'Start work?', confirmationMessage: 'Choose worker' }
      }

      if (!act) {
        return
      }

      if (act.linkFormula || action == 'start') {
        if (act.objectAction) {
          if (!this.executeObjectAction(act.objectAction, item)) {
            return
          }
        }

        var title = this.evaluateMessage(act.title, item)
        if (title.length > 32)
          title = '<h3>' + title + '</h3>'
        // const that = this
        this.$swal({
          title: title,
          html: this.evaluateMessage(act.confirmationMessage, item),
          icon: 'question',
          input: 'select',
          inputOptions: this.startActionOptions || {},
          inputValue: item.workerId,
          showCancelButton: true,
          confirmButtonText: this.evaluateMessage(
            act.buttonTitle || 'OK',
            item
          ),

        }).then((result) => {
          if (result && result.isConfirmed && result.value) {
            this.$emit(action, item, result.value)
          }
        })
      } else {
        this.$swal({
          title: this.evaluateMessage(act.title, item),
          html: this.evaluateMessage(act.confirmationMessage, item),
          icon: 'warning',
          showCancelButton: true,
        }).then((result) => {
          if (result && result.isConfirmed) {
            this.$emit(action, item)
          }
        })
      }
    },

    async executeObjectAction(objectAction, item) {
      var fn = compileFormula(objectAction)
      var a = fn ? fn.call(this, item, item, this, undefined, undefined, item) : objectAction
      if (a.startsWith('method:')) {
        var methodCall = a.substring('method:'.length)
        var methodName = this.getMethodName(methodCall)
        var paramsArr = this.getMethodArgs(methodCall, item)
        if (methodName == 'openFileDocument' && paramsArr.length > 0) {
          this.openFileDocument(paramsArr[0])
          return true
        } else if (methodName == 'void') {
          return true
        } else {
          console.error('Invalid method call: ' + objectAction)
        }
      }

      return false
    },

    getMethodName(methodCall) {
      var paramsStart = methodCall.indexOf('(')
      return paramsStart <= 0 ? null : methodCall.substring(0, paramsStart)
    },

    getMethodArgs(methodCall, item) {
      var result = []

      var paramsStart = methodCall.indexOf('(')
      var paramsEnd = methodCall.indexOf(')')
      if (paramsStart <= 0 || paramsEnd <= 0 || paramsStart > paramsEnd) {
        console.error('Invalid method call: ' + methodCall)
        return result
      }

      var params = methodCall.substring(paramsStart + 1, paramsEnd)
      params.split(',').forEach((p) => {
        var fn = compileFormula(p.trim())
        result.push(fn ? fn.call(this, item, item, this, undefined, undefined, item) : undefined)
      })
      return result
    },

    getItem(row, colId) {
      var hierObj = this.findHierObjectAndPropertyName(row, colId)
      return hierObj.entity
        ? hierObj.entity[hierObj.propName]
        : null
    },

    shouldShowFolded(row, colId) {
      if (!this.isFoldableTable) {
        return false
      }

      var item = this.getItem(row, colId)
      return item && item.length > 2 && this.isRowFolded(row)
    },

    isRowFolded(row) {
      return !row.__unfolded
    },

    switchFoldState(row) {
      if (this.isRowFoldable(row)) {
        // Vue.set(row, '__unfolded', !row.__unfolded)
        row['__unfolded'] = !row.__unfolded // todo - test
      }
    },

    // Pure per-row foldability calc (no caching). Shared by the eager
    // precomputeFoldable() pass and the lazy isRowFoldable() fallback.
    calcRowFoldable(row) {
      if (this.metadata && this.metadata.columns) {
        for (var i = 0; i < this.metadata.columns.length; i++) {
          var col = this.metadata.columns[i]
          if (
            this.isFilesArray(col) ||
            this.isComplexArray(col) ||
            this.isLinkArray(col)
          ) {
            var item = this.getItem(row, col.id)
            if (item && item.length > 2) {
              return true
            }
          }
        }
      }
      return false
    },

    // Warm the fold cache for every row in one pass (called from the items /
    // metadata watchers) so the first paint doesn't pay the columns × items
    // walk that the lazy isRowFoldable() fallback would otherwise incur.
    precomputeFoldable() {
      if (!this.isFoldableTable || !this.items) {
        return
      }
      this.items.forEach((row) => {
        row.__isFoldable = this.calcRowFoldable(row)
        row.__isFoldableCalculated = true
      })
    },

    isRowFoldable(row) {
      if (!this.isFoldableTable) {
        return false
      }

      // Fallback for rows the precompute didn't cover (e.g. metadata not yet
      // ready when items arrived, or a row added after the initial pass).
      if (!row.__isFoldableCalculated) {
        row.__isFoldableCalculated = true
        row.__isFoldable = this.calcRowFoldable(row)
      }

      return row.__isFoldable
    },

    getFoldedItemText(row, colId) {
      var item = this.getItem(row, colId)
      return item && item.length ? `... x${item.length}` : ''
    },

    getFoldButtonIcon(row) {
      return this.isRowFolded(row) ? 'Plus' : 'Minus'
    },

    getItemText(row, col, temp) {
      var res = this.getItem(row, col.id)
      if (col.suffix) {
        res += ' ' + col.suffix
      }

      if (col.type == 'dateLabel' || col.type == 'statusDateLabel') {
        res = this.getFormattedDate(res)
      }
      else if (col.type == 'pricePerDurationUnits') { 
        res = this.toUserPricePerDurationUnits(res)
      }
      else if (col.type == 'durationInUserUnits') {
        res = this.toDurationInUserUnits(res)
      }
      

      return res
    },

    /* Moved to main
    getFormattedDate(src) {
      if (this.globalMetadata && this.globalMetadata.dateFormat && src) {
        var d = new Date(src)
        src = this.date2str(d, this.globalMetadata.dateFormat)
      }

      return src
    },

    date2str(x, y) {
      var z = {
        M: x.getMonth() + 1,
        d: x.getDate(),
        h: x.getHours(),
        m: x.getMinutes(),
        s: x.getSeconds(),
      }
      y = y.replace(/(M+|d+|h+|m+|s+)/g, function (v) {
        return ((v.length > 1 ? '0' : '') + eval('z.' + v.slice(-1))).slice(-2)
      })

      return y.replace(/(y+)/g, function (v) {
        return x.getFullYear().toString().slice(-v.length)
      })
    },*/

    getLinkArrayItemText(itm) {
      // todo - for now it is implemented only for material order item linked components
      // todo - display text if different from 'id' must be specified in metadata

      if (itm.externalRefId && itm.externalRefId.length > 0) {
        return `${itm.externalRefId} (${itm.id})`
      } else if (
        itm.component &&
        itm.component.externalRefId &&
        itm.component.externalRefId.length > 0
      ) {
        return itm.component.externalRefId
      } else if (itm.orderId) {
        return `${itm.orderId} (${itm.id})`
      } else if (!!itm.name && itm.name.length > 0) {
        return itm.name
      } else if (itm.value && itm.value.length > 0) {
        return itm.value
      } else {
        return itm.id
      }
    },

    getArrayItemClass(col, itm) {
      var duplicatedArray = this.duplicatedItemsByCol[col.id]
      if (
        duplicatedArray &&
        duplicatedArray.find((n) => n === this.getLinkArrayItemText(itm))
      ) {
        return 'duplicated-item'
      }

      return ''
    },

    getItemIfDuplicatedClass(col, itm) {
      var duplicatedArray = this.duplicatedItemsByCol[col.id]

      if (duplicatedArray && duplicatedArray.find((n) => n === itm)) {
        return 'duplicated-item'
      }

      return ''
    },

    getStatusLabelClass(row, colId) {
      if (row.alerts && row.alerts.length > 0) {
        return `status-label in-progress-warning item-alerts`
      }

      var hierObj = colId 
        ? this.findHierObjectAndPropertyName(row, colId)
        : {entity: row, propName: ''};

      var statuses = this.statusesMap
      if (statuses && hierObj.entity && hierObj.entity.status) {
        var st =
          statuses[hierObj.entity.status] ||
          statuses[hierObj.entity[hierObj.propName]]
        if (st) {
          return `status-label ${st.class}`
        }
      }

      return ''
    },

    tableCellClassName(data) {
      var row = data.row
      var columnIndex = data.columnIndex

      if (
        !this.metadata ||
        !this.metadata.columns ||
        !this.metadata.columns[columnIndex]
      ) {
        return ''
      }

      var column = this.metadata.columns[columnIndex]
      if (this.isStatusLabel(column)) {
        return this.getStatusLabelClass(row, column.id)
      } else if(column.type == 'statusComplexArray') {
        return this.getStatusLabelClass(row, column.id.replace(/\.workers$/, '.actionTypeId'))
      }

      if(row.__highlight) 
        return 'row-highlight';

      return ''
    },

    getFooterClass() {
      return this.nonFixedFooter ? 'button-create-new' : 'footer fixed'
    },

    onItemChanged(entity, propName) {
      this.$emit('onItemValueChanged', entity, propName)
    },

    onAction(action, entity) {
      if (action == 'create') {
        this.$emit('create', entity)
      } else {
        this.$emit('onAction', action, entity)
      }
    },

    isFirst(entity) {
      return this.itemsList && this.itemsList.length > 0
        ? this.itemsList[0].id == entity.id
        : false
    },

    isLast(entity) {
      return this.itemsList && this.itemsList.length > 0
        ? this.itemsList[this.itemsList.length - 1].id == entity.id
        : false
    },

    getHint(row, col) {
      if (!col || !col.hint) {
        if (this.isStatusLabel(col)) {
          return row.status
        }
      }

      const rootEntity = row
      const item = row
      const that = this

      var fn = compileFormula(col.hint)
      try {
        var result = fn ? fn.call(this, rootEntity, item, that, col, col, row) : null
        if (this.isStatusLabel(col)) {
          result = `${row.status}<br/><br/>${result ?? ''}`
        }
        return result
      } catch (e) {
        console.error(`ERROR: getHint eval(${col.hint}) -> ${e.message}`)
      }

      return null
    },

    hasHint(row, col) {
      var h = this.getHint(row, col)
      return h && h.length > 0
    },
  },
}
</script>

<style scoped>
.column-actions-wrapper {
  text-align: right;
}

.column-actions {
  display: inline-flex;
}

.el-select-fix {
  -moz-appearance: unset;
  -webkit-appearance: unset;
  appearance: unset;
  width: 100%;
}

.button-create-new {
  margin-top: 8px;
  margin-bottom: 8px;
}

.duplicated-item {
  border: 1px dotted var(--main-menu-active-text-color);
  padding: 2px;
}

.fold-button {
  color: var(--main-text-color);
  font-weight: bold !important;

  font-weight: bold;
  font-size: medium;
}

.status-label .cell {
  height: inherit;
  text-align: center;
  border-spacing: 8px;
}

.complex-array-item.status-label {
  margin-left: -10px;
  margin-bottom: 4px;
  padding-left: 4px;
  border: solid 1px;
}

.word-wrap {
  text-wrap: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Collapsed (text) state of a lazy-edit select cell. A CSS-only caret hints
   that it's a dropdown without mounting any icon/select component. */
.lazy-select-text {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 24px;
}

.lazy-select-text::after {
  content: '';
  width: 0;
  height: 0;
  border: 4px solid transparent;
  border-top-color: var(--el-text-color-placeholder, #a8abb2);
  margin-top: 4px;
}

.tooltip-multiline {
  white-space: pre-line;
}

</style>
